import { Button, Image } from '../../modules/design-system';
import { RarityBadge } from '../../modules/design-system/RarityBadge';
import type { JustPulledItem } from '../../modules/api';
import { formatRelative } from './relativeTime';

const PHOTO_BG =
  'linear-gradient(180deg, #C2C3C5 -16.4%, #F7F8F8 121.88%), #FFF';

// Background flows through Design Constructor knobs (`--co-JustPulledCard-*`).
// Defaults reproduce the previous hardcoded glass wash so unmodified
// deployments stay pixel-identical.
const GLASS_BG =
  'linear-gradient(' +
  'var(--co-JustPulledCard-bgDirection, to bottom), ' +
  'color-mix(in srgb, var(--co-JustPulledCard-bgFrom, rgba(255,255,255,0.20)) ' +
  'calc(var(--co-JustPulledCard-bgOpacity, 1) * 100%), transparent) 0%, ' +
  'color-mix(in srgb, var(--co-JustPulledCard-bgTo, rgba(153,153,153,0.20)) ' +
  'calc(var(--co-JustPulledCard-bgOpacity, 1) * 100%), transparent) 100%)';



export interface JustPulledCardProps {
  item: JustPulledItem;
  /** Tapping "Rip Repackz" jumps to the pack page for this offer. */
  onRip?: (offerId: number) => void;
}

/**
 * Full pulled-card cell used by the Just Pulled list page (Figma 226:374
 * desktop · 226:478 mobile). The card stack is a glassy gradient panel:
 *   • a light-gradient square hero with the card photo centred
 *   • a "RARE" pill anchored top-centre (only for non-Common badges)
 *   • title below the hero
 *   • bottom row: pack thumbnail + pack title + relative time + Rip CTA
 *
 * Username/avatar pill from the Figma is intentionally omitted until the
 * iframe Just Pulled API exposes the pulling user's identity.
 */
export function JustPulledCard({ item, onRip }: JustPulledCardProps) {
  const isRare = isRareBadge(item.badge);
  const ago = formatRelative(item.openedAt);
  const offer = item.offer;

  return (
    <article
      data-co-component="JustPulledCard"
      className="
        relative w-full overflow-hidden [border-radius:var(--co-JustPulledCard-borderRadius,28px)]
        shadow-[0_9px_30px_0_rgba(7,10,18,0.08)]
        @[600px]:shadow-[0_64px_34px_0_rgba(0,0,0,0.05),0_9px_30px_0_rgba(7,10,18,0.08)]
      "
      style={{
        backgroundImage: GLASS_BG,
        backdropFilter: 'blur(var(--co-JustPulledCard-glassBlur, 27px))',
        WebkitBackdropFilter: 'blur(var(--co-JustPulledCard-glassBlur, 27px))',
        borderTopStyle: 'solid',
        borderBottomStyle: 'solid',
        borderTopWidth: 'var(--co-JustPulledCard-borderWidth, 1px)',
        borderBottomWidth: 'var(--co-JustPulledCard-borderWidth, 1px)',
        borderColor: 'var(--co-JustPulledCard-borderColor, rgba(171,174,179,0.24))',
      }}
    >
      <div
        className="relative m-2 aspect-square overflow-hidden rounded-[25px]"
        style={{ background: PHOTO_BG }}
      >
        {item.imageUrl && (
          <Image
            src={item.imageUrl}
            alt={item.title || 'Pulled card'}
            className="absolute inset-0 h-full w-full"
            imgClassName="absolute inset-0 h-full w-full object-contain p-12"
            quality={92}
          />
        )}

        {isRare && item.badge?.name && (
          <RarityBadge
            variant="sticker"
            className="absolute left-1/2 top-0 -translate-x-1/2"
          >
            {item.badge.name}
          </RarityBadge>
        )}

      </div>

      <div className="px-4 pt-2">
        <p
          className="line-clamp-2 font-semibold leading-snug tracking-[-0.3px]"
          style={{
            color: 'var(--co-JustPulledCard-titleColor, #ffffff)',
            fontFamily: 'var(--co-JustPulledCard-titleFont), Inter, system-ui, sans-serif',
            fontSize: 'var(--co-JustPulledCard-titleSize, 15px)',
          }}
        >
          {item.title}
        </p>
      </div>

      <div className="flex items-center gap-3 px-4 pb-4 pt-3">
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div
            className="
              relative size-11 shrink-0 overflow-hidden rounded-[8px]
              bg-[color:var(--color-bg-card)]
            "
          >
            {offer?.imageUrl && (
              <Image
                src={offer.imageUrl}
                alt=""
                className="absolute inset-0 h-full w-full"
                imgClassName="absolute inset-0 h-full w-full object-contain p-1"
                quality={88}
              />
            )}
          </div>
          <div className="flex min-w-0 flex-col">
            <p
              className="truncate leading-snug"
              style={{
                color: 'var(--co-JustPulledCard-offerNameColor, #ffffff)',
                fontFamily:
                  'var(--co-JustPulledCard-offerNameFont), Inter, system-ui, sans-serif',
                fontSize: 'var(--co-JustPulledCard-offerNameSize, 13px)',
              }}
            >
              {offer?.title || 'Pack'}
            </p>
            {ago && (
              <p
                className="truncate leading-snug"
                style={{
                  color: 'var(--co-JustPulledCard-timeAgoColor, rgba(255,255,255,0.6))',
                  fontFamily:
                    'var(--co-JustPulledCard-timeAgoFont), Inter, system-ui, sans-serif',
                  fontSize: 'var(--co-JustPulledCard-timeAgoSize, 13px)',
                }}
              >
                {ago}
              </p>
            )}
          </div>
        </div>

        <Button
          variant="accent-gloss"
          size="md"
          onClick={offer && onRip ? () => onRip(offer.id) : undefined}
          disabled={!offer || !onRip}
        >
          <span className="@[420px]:inline hidden">Rip Repackz</span>
          <span className="@[420px]:hidden">Rip</span>
        </Button>
      </div>
    </article>
  );
}

function isRareBadge(badge: JustPulledItem['badge']): boolean {
  if (!badge) return false;
  if (badge.id === null) return false;
  return badge.id !== 1;
}
